import { Button } from '@/components/form/Button';
import { Form } from '@/components/form/Form';
import { ButtonVariant } from '@/components/form/buttonHelper';
import { Property } from '@/components/form/fields/select/SelectInput';
import { useModal } from '@/components/modal/useModal';
import SiEye from '@/icons/SiEye';
import SiSettings from '@/icons/SiSettings';
import SiShuffle from '@/icons/SiShuffle';
import { ImageDetails, PropertyBag } from '@/services/api/contracts/catalog/types';
import { useDeleteProduct, useProduct, useProducts, useUpdateProduct } from '@/services/api/hooks/product';
import { BasicBetterBest, useBasicBetterBestOptions } from '@/services/catalogue/useBasicBetterBest';
import { zodResolver } from '@hookform/resolvers/zod';
import { Value } from '@udecode/plate-common';
import { FC, PropsWithChildren } from 'react';
import { useForm } from 'react-hook-form';
import LocalizedStrings from 'react-localization';
import { useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';
import { z } from 'zod';
import { Page } from '../../../layouts/Page';
import CostAndMargin from './CostAndMargin';
import HideProductModal from './HideProductModal';
import { BaseProductProperties, ProductDescription, ProductForm, ProductSidebar } from './ProductForm';
import ReplaceProductModal from './ReplaceProductModal';
import EditProductCustomerRestrictions from './customerRestrictions/edit/EditProductCustomerRestrictions';

const strings = new LocalizedStrings({
  en: {
    editProductInformation: 'Edit product information',
    delete: 'Delete',
    cancel: 'Cancel',
    updateAndPublish: 'Update & publish',
    visibility: 'Visibility',
    hideProduct: 'Hide product',
    replaceProduct: 'Replace product',
    addFutureChange: 'Add future change',
    costAndMargin: 'Cost & margin',
    productUpdated: 'Product {0} updated',
    productDeleted: 'Product {0} deleted'
  }
});

interface EditProductProps {}

export const EditProductFormFields = z.object({
  productId: z.string(),
  code: z.string(),
  description: z.custom<Value>(),
  propertyBag: PropertyBag,
  imageUri: ImageDetails.optional(),
  manufacturer: z.custom<Property>(),
  category: z.custom<Property>(),
  name: z.string(),
  basicBetterBest: z.custom<Property>(),
  region: z.custom<Property>(),
  isRateCardItem: z.boolean().optional(),
  triggerTypes: z.array(z.string()).optional().nullable(),
  maxChargeableWeightKg: z.number().optional(),
  requiresSerialNumber: z.boolean().optional(),
  basePackForRequestType: z.string().optional(),
  applicableRequestTypes: z.array(z.string()).optional()
});

export type EditProductFormFields = z.infer<typeof EditProductFormFields>;

export const EditUploadFormFields = z.object({
  productId: z.string(),
  code: z.string().optional(),
  description: z.custom<Value>(),
  propertyBag: PropertyBag,
  imageUri: ImageDetails.optional(),
  manufacturer: z.custom<Property>(),
  category: z.custom<Property>(),
  name: z.string(),
  basicBetterBest: z.custom<Property>(),
  region: z.custom<Property>(),
  isRateCardItem: z.boolean().optional(),
  triggerTypes: z.array(z.string()).optional().nullable(),
  maxChargeableWeightKg: z.number().optional(),
  basePackForRequestType: z.string().optional(),
  applicableRequestTypes: z.array(z.string()).optional()
});

export type EditUploadFormFieldsNoCode = z.infer<typeof EditUploadFormFields>;

export const EditProduct: FC<PropsWithChildren<EditProductProps>> = () => {
  const EditProductForm = () => {
    const { productId } = useParams<{ productId: string }>();
    if (!productId) throw new Error('Missing productId');

    const navigate = useNavigate();

    const updateProduct = useUpdateProduct();
    const deleteProduct = useDeleteProduct();
    const [product] = useProduct(productId);
    const [products] = useProducts();
    const bbbOptions = useBasicBetterBestOptions();

    const methods = useForm<EditProductFormFields>({
      defaultValues: {
        ...product,
        imageUri: product.imageUri
          ? Object.keys(product.imageUri).length > 0
            ? product.imageUri
            : undefined
          : undefined,
        // @ts-expect-error TODO: fix this
        description: product.description.value,
        propertyBag: product.propertyBag?.properties ?? [],
        basicBetterBest: bbbOptions.find(o => o.id === product.propertyBag.basicBetterBest) || bbbOptions[0],
        isRateCardItem: product.isRateCardItem ?? false,
        triggerTypes: product.propertyBag?.triggerTypes ?? [],
        maxChargeableWeightKg: product.propertyBag?.maxChargeableWeightKg ?? 0,
        basePackForRequestType: product.propertyBag?.basePackForRequestType ?? 'None',
        applicableRequestTypes: product.propertyBag?.applicableRequestTypes ?? []
      },
      resolver: zodResolver(EditProductFormFields)
    });

    const onSubmit = async (data: EditProductFormFields) => {
      if (data.isRateCardItem && data.basePackForRequestType && data.basePackForRequestType !== 'None') {
        const existingBasePack = products?.find(
          p =>
            p.isRateCardItem &&
            p.region?.id === data.region?.id &&
            p.propertyBag?.basePackForRequestType === data.basePackForRequestType &&
            p.id !== productId
        );
        if (existingBasePack) {
          toast.error('A Base Pack for this request type already exists in this Region.');
          return;
        }
      }

      const propertyBagPayload: any = {
        properties: data.propertyBag,
        basicBetterBest: data.basicBetterBest.id as BasicBetterBest
      };

      if (data.isRateCardItem) {
        if (data.triggerTypes) propertyBagPayload.triggerTypes = data.triggerTypes;
        if (data.maxChargeableWeightKg !== undefined) propertyBagPayload.maxChargeableWeightKg = data.maxChargeableWeightKg;
        if (data.basePackForRequestType && data.basePackForRequestType !== 'None') propertyBagPayload.basePackForRequestType = data.basePackForRequestType;
        if (data.applicableRequestTypes) propertyBagPayload.applicableRequestTypes = data.applicableRequestTypes;
      }

      const product = await updateProduct.mutateAsync({
        ...data,
        id: productId,
        categoryId: data.category.id,
        manufacturerId: data.manufacturer.id,
        propertyBag: propertyBagPayload,
        description: {
          value: data.description
        },
        regionId: data.region.id,
        isRateCardItem: data.isRateCardItem
      });
      toast.success(strings.formatString(strings.productUpdated, product.name));
      navigate(`/configuratorCatalogue/${product.category.id}`);
    };

    const hideProductModalProps = useModal<{ productId: string }>();
    const replaceProductModalProps = useModal<{ productId: string }>();

    const onHideProductClick = () => {
      hideProductModalProps.open({ productId });
    };

    const onReplaceProductClick = () => {
      replaceProductModalProps.open({ productId });
    };

    return (
      <>
        <HideProductModal {...hideProductModalProps} />
        <ReplaceProductModal {...replaceProductModalProps} />

        <Form methods={methods} onSubmit={onSubmit} className="flex w-full flex-1 flex-col overflow-hidden px-8 pt-8">
          {methods.watch('isRateCardItem') && (
            <div className="mb-6 p-5 bg-black marching-ants shadow-[8px_8px_0px_#55FFFF] rounded-none font-mono text-[#55FFFF] uppercase tracking-tight">
              <div className="flex items-center gap-3">
                <strong className="font-extrabold animate-pulse text-[#FF55FF] text-xl">⚠️ WARNING:</strong> 
                <span className="font-bold">Rate Cards should only be edited in <SiSettings className="inline-block h-5 w-5 mb-1" /> \ Rate Cards. Saving changes here may permanently corrupt the item's complex property configuration.</span>
              </div>
            </div>
          )}
          <div className="mb-8 flex w-full items-center justify-between">
            <h2 className="flex items-center gap-2 text-3xl font-bold tracking-tight">
              <span>{strings.editProductInformation}</span>
            </h2>
            <div className="flex gap-2">
              <Button
                type="button"
                variant={ButtonVariant.Secondary}
                onClick={async () => {
                  await deleteProduct.mutateAsync({
                    id: productId
                  });
                  toast.success(strings.formatString(strings.productDeleted, product.name));
                  navigate(`/configuratorCatalogue/${product.category.id}`);
                }}
              >
                {strings.delete}
              </Button>
              <Button type="button" onClick={() => navigate(`/configuratorCatalogue/${product.category.id}`)}>
                {strings.cancel}
              </Button>
              <Button variant={ButtonVariant.Primary} loading={updateProduct.isLoading} type="submit">
                {strings.updateAndPublish}
              </Button>
            </div>
          </div>
          <ProductForm>
            <ProductDescription />
            <ProductSidebar>
              <div className="flex flex-col gap-2">
                <label className="text-lg font-medium">{strings.visibility}</label>
                <div className="flex flex-row gap-2">
                  <Button type="button" variant={ButtonVariant.Ghost} onClick={onHideProductClick}>
                    <SiEye className="h-6 w-6" /> <span className="text-md font-medium">{strings.hideProduct}</span>
                  </Button>
                  <Button type="button" variant={ButtonVariant.Ghost} onClick={onReplaceProductClick}>
                    <SiShuffle className="h-6 w-6" />{' '}
                    <span className="text-md font-medium">{strings.replaceProduct}</span>
                  </Button>
                </div>
              </div>
              <BaseProductProperties />
              <CostAndMargin productId={productId} />
              <EditProductCustomerRestrictions productId={productId} />
            </ProductSidebar>
          </ProductForm>
        </Form>
      </>
    );
  };
  return (
    <Page className="flex">
      <EditProductForm />
    </Page>
  );
};
